import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import Icon from './Icon';

interface Bid {
  id: string;
  bidderId: string;
  bidderName: string;
  amount: number;
  timestamp: Date | string;
}

interface LiveBidFeedProps {
  bids: Bid[];
  currentUserId?: string;
  maxItems?: number;
  showAnimation?: boolean;
}

export default function LiveBidFeed({ 
  bids, 
  currentUserId,
  maxItems = 10,
  showAnimation = true 
}: LiveBidFeedProps) {
  const [animatedBids, setAnimatedBids] = useState<Set<string>>(new Set());

  // Track new bids for animation
  useEffect(() => {
    if (bids.length > 0 && showAnimation) {
      const latestBid = bids[0];
      setAnimatedBids(prev => new Set([...prev, latestBid.id]));
      
      // Remove animation after 1 second
      const timer = setTimeout(() => {
        setAnimatedBids(prev => {
          const next = new Set(prev);
          next.delete(latestBid.id);
          return next;
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [bids, showAnimation]);

  const displayBids = bids.slice(0, maxItems);

  if (displayBids.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Icon name="notifications" className="text-4xl mb-2 opacity-50" />
        <p>No bids yet. Be the first to bid!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="local_activity" size="small" />
          Live Bid Feed
        </h3>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-xs text-gray-500">Live</span>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {displayBids.map((bid, index) => {
          const isCurrentUser = bid.bidderId === currentUserId;
          const isLatest = index === 0;
          const isAnimating = animatedBids.has(bid.id);
          const timestamp = typeof bid.timestamp === 'string' 
            ? new Date(bid.timestamp) 
            : bid.timestamp;

          return (
            <div
              key={bid.id}
              className={clsx(
                'py-3 transition-all duration-300',
                isAnimating && 'bg-green-50 -mx-2 px-2 rounded-lg',
                isLatest && !isAnimating && 'bg-primary-50 -mx-2 px-2 rounded-lg'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon 
                    name={isLatest ? 'emoji_events' : isCurrentUser ? 'person' : 'person_outline'} 
                    className={clsx(
                      isLatest ? 'text-yellow-500' : 
                      isCurrentUser ? 'text-primary-600' : 'text-gray-400'
                    )}
                  />
                  <div>
                    <span className={clsx(
                      'font-medium',
                      isCurrentUser && 'text-primary-600'
                    )}>
                      {isCurrentUser ? 'You' : bid.bidderName}
                    </span>
                    {isLatest && (
                      <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                        Winning
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={clsx(
                    'font-bold',
                    isLatest ? 'text-primary-600 text-lg' : 'text-gray-900'
                  )}>
                    £{(bid.amount / 100).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(timestamp, { addSuffix: true })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {bids.length > maxItems && (
        <p className="text-center text-sm text-gray-500 pt-2">
          +{bids.length - maxItems} earlier bids
        </p>
      )}
    </div>
  );
}
